import { ApiError, type FetchOptions, type ProblemDetails } from './types';

/**
 * Fetch wrapper z Bearer token injection (Auth0), ProblemDetails parsing, toast on error.
 *
 * Wzorzec z Budowa360.Webapp/src/api/client.ts:
 * - `setTokenProvider(getAccessTokenSilently)` w main.tsx wstrzykuje sposób pobierania tokenu.
 * - Wszystkie HTTP methods auto-attach Bearer header (chyba że endpoint anonymous).
 * - 4xx/5xx parsuje ProblemDetails RFC 7807 → ApiError (z field errors).
 * - Non-field errors triggerują global toast (T27 wires real toast UI).
 * - 403 z `code: 'subscription-blocked'` (Faza 5) emituje custom event `subscription-blocked`.
 */

// Default `/api` relative — Vite dev proxy (vite.config.ts) przekierowuje na :7071 server-side,
// browser widzi tylko same-origin (zero CORS). Cloud override przez VITE_API_BASE_URL=https://api.fiszki.dev
// w GitHub Environment vars (frontend.yml build).
const API_BASE_URL = import.meta.env['VITE_API_BASE_URL'] ?? '/api';

type TokenProvider = () => Promise<string | null | undefined>;

let tokenProvider: TokenProvider | null = null;

export function setTokenProvider(provider: TokenProvider): void {
  tokenProvider = provider;
}

async function getAuthToken(): Promise<string | null> {
  if (!tokenProvider) return null;
  try {
    const token = await tokenProvider();
    return token ?? null;
  } catch {
    return null;
  }
}

async function buildHeaders(extra?: HeadersInit): Promise<Headers> {
  const headers = new Headers(extra ?? {});
  if (!headers.has('Content-Type')) {
    headers.set('Content-Type', 'application/json');
  }
  if (!headers.has('Accept')) {
    headers.set('Accept', 'application/json');
  }
  const token = await getAuthToken();
  if (token && !headers.has('Authorization')) {
    headers.set('Authorization', `Bearer ${token}`);
  }
  return headers;
}

async function parseResponse<T>(res: Response, options?: FetchOptions): Promise<T> {
  if (res.ok) {
    if (res.status === 204) {
      return undefined as T;
    }
    const text = await res.text();
    return text.length > 0 ? (JSON.parse(text) as T) : (undefined as T);
  }

  let problem: ProblemDetails;
  try {
    problem = (await res.json()) as ProblemDetails;
  } catch {
    problem = {
      title: res.statusText || 'Unknown error',
      status: res.status,
    };
  }

  const error = new ApiError(problem);

  if (!options?.suppressErrorToast) {
    // T27 wire'uje real ToastContainer. Tymczasowo: window event.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('api-error', { detail: { problem } }));
    }
  }

  // Faza 5: 403 z subscription-blocked code → redirect to billing.
  if (res.status === 403 && problem.type === 'subscription-blocked') {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('subscription-blocked'));
    }
  }

  throw error;
}

async function fetchWithTimeout(
  input: string,
  init: RequestInit & { timeoutMs?: number },
): Promise<Response> {
  const controller = new AbortController();
  const timeoutMs = init.timeoutMs ?? 30_000;
  const timer = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await fetch(input, { ...init, signal: controller.signal });
  } finally {
    clearTimeout(timer);
  }
}

function buildUrl(path: string): string {
  if (path.startsWith('http://') || path.startsWith('https://')) return path;
  const base = API_BASE_URL.replace(/\/$/, '');
  const p = path.startsWith('/') ? path : `/${path}`;
  return `${base}${p}`;
}

export async function fetchJson<T>(path: string, options?: FetchOptions): Promise<T> {
  const headers = await buildHeaders(options?.headers);
  const res = await fetchWithTimeout(buildUrl(path), {
    ...options,
    method: 'GET',
    headers,
  });
  return parseResponse<T>(res, options);
}

export async function postJson<TResponse, TBody = unknown>(
  path: string,
  body?: TBody,
  options?: FetchOptions,
): Promise<TResponse> {
  const headers = await buildHeaders(options?.headers);
  const res = await fetchWithTimeout(buildUrl(path), {
    ...options,
    method: 'POST',
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  return parseResponse<TResponse>(res, options);
}

export async function patchJson<TResponse, TBody = unknown>(
  path: string,
  body: TBody,
  options?: FetchOptions,
): Promise<TResponse> {
  const headers = await buildHeaders(options?.headers);
  const res = await fetchWithTimeout(buildUrl(path), {
    ...options,
    method: 'PATCH',
    headers,
    body: JSON.stringify(body),
  });
  return parseResponse<TResponse>(res, options);
}

export async function putJson<TResponse, TBody = unknown>(
  path: string,
  body: TBody,
  options?: FetchOptions,
): Promise<TResponse> {
  const headers = await buildHeaders(options?.headers);
  const res = await fetchWithTimeout(buildUrl(path), {
    ...options,
    method: 'PUT',
    headers,
    body: JSON.stringify(body),
  });
  return parseResponse<TResponse>(res, options);
}

export async function deleteJson<TResponse = void>(
  path: string,
  options?: FetchOptions,
): Promise<TResponse> {
  const headers = await buildHeaders(options?.headers);
  const res = await fetchWithTimeout(buildUrl(path), {
    ...options,
    method: 'DELETE',
    headers,
  });
  return parseResponse<TResponse>(res, options);
}

/**
 * Wyśle raw bytes (Blob/File) do nasz backend POST endpoint z user-supplied Content-Type.
 * Pomija JSON body wrapping z `postJson` — używane np. dla avatar upload (POST /me/avatar)
 * gdzie backend czyta `req.Body` jako bytes a nie deserializuje JSON.
 */
export async function uploadRawBytes<TResponse>(
  path: string,
  body: Blob,
  contentType: string,
  options?: FetchOptions,
): Promise<TResponse> {
  // buildHeaders forsuje Content-Type:application/json gdy brak — przekazujemy explicit
  // override przez Headers instance. Accept zostaje json (response z backendu).
  const merged = new Headers(options?.headers);
  merged.set('Content-Type', contentType);
  const headers = await buildHeaders(merged);
  const res = await fetchWithTimeout(buildUrl(path), {
    ...options,
    method: 'POST',
    headers,
    body,
  });
  return parseResponse<TResponse>(res, options);
}

/** Direct upload do Azure Blob Storage przez SAS URL (Faza 3). */
export async function uploadToUrl(
  url: string,
  bytes: Blob | ArrayBuffer,
  contentType: string,
  _options?: { onProgress?: (loaded: number, total: number) => void },
): Promise<void> {
  const res = await fetch(url, {
    method: 'PUT',
    headers: {
      'x-ms-blob-type': 'BlockBlob',
      'Content-Type': contentType,
    },
    body: bytes,
  });
  if (!res.ok) {
    throw new Error(`uploadToUrl failed: ${res.status} ${res.statusText}`);
  }
}
